import {
  ActionFunctionArgs,
  Form,
  LoaderFunctionArgs,
  redirect,
  useLoaderData,
} from "react-router-dom";
import toast from "react-hot-toast";
import { CREATE_ORGANIZATION } from "./mutations";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { Input } from "../../components/inputs/Input";
import { FormLayout } from "../../layout/FormLayout";
import { organizationStore } from "../../store/organization";
import { userStore } from "../../store/user";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import { GET_ORGANIZATION } from "./queries";
import { Organization } from "../../types/organization";

const client = createApolloClient();
const { getState } = organizationStore;
const { getState: getUserStore } = userStore;

export const organizationCloneLoader = async ({
  params,
}: LoaderFunctionArgs) => {
  const { id } = params;
  try {
    const { data } = await client.query({
      query: GET_ORGANIZATION,
      variables: { id },
    });
    return data.organizations_by_pk;
  } catch (error) {
    return toast.error("Could not fetch organization data");
  }
};

export const organizationCloneAction = async ({
  request,
}: ActionFunctionArgs) => {
  const user = getUserStore().user;
  const fetchOrganizations = getState().fetchOrganizations;
  const body = await request.formData();
  const inputData = Object.fromEntries(body);
  const name = inputData.name as string;

  if (!name) {
    return toast.error("Please fill out all required fields.");
  }

  try {
    await client.mutate({
      mutation: CREATE_ORGANIZATION,
      variables: {
        input: {
          ...inputData,
          last_updated_by: user?.id,
        },
      },
    });
    toast.success("Organization Cloned");
    await fetchOrganizations();
    return redirect("/organizations/list");
  } catch (error) {
    return toast.error("Failed to clone organization: " + error);
  }
};

export const OrganizationsClone = () => {
  const organization = (useLoaderData() as Organization) || { name: "Default" };
  const clonedOrganizationName = `${organization.name} - clone`;
  return (
    <Form action={`/organizations/${organization.id}/clone`} method="post">
      <FormLayout>
        <div className="col-span-3 sm:col-span-3">
          <Input
            label="Name"
            type="text"
            name="name"
            required
            defaultValue={clonedOrganizationName}
          />
        </div>
        <IndeterminateCheckbox
          label="Is Active?"
          name="is_active"
          defaultChecked={organization.is_active}
        />
        <div className="col-span-6 grid grid-cols-6 gap-5 bg-gray-100 p-2 rounded">
          <label className="col-span-6 block text-sm font-medium text-gray-700">
            Invoicing Configuration
          </label>
          <div className="col-span-6 sm:col-span-3">
            <Input
              label="Logo URL"
              name="logo_url"
              type="text"
              defaultValue={organization.logo_url}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Input
              label="Background URL"
              name="background_url"
              type="text"
              defaultValue={organization.background_url}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Input
              label="Business Name"
              name="business_name"
              type="text"
              defaultValue={organization.business_name}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Input
              label="Business Address 1"
              name="business_address_1"
              type="text"
              defaultValue={organization.business_address_1}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Input
              label="Business Address 2"
              name="business_address_2"
              type="text"
              defaultValue={organization.business_address_2}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Input
              label="Business City"
              name="business_city"
              type="text"
              defaultValue={organization.business_city}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Input
              label="Business State"
              name="business_state"
              type="text"
              defaultValue={organization.business_state}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Input
              label="Business Zipcode"
              name="business_zipcode"
              type="text"
              defaultValue={organization.business_zipcode}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Input
              label="Business Website"
              name="business_website"
              type="text"
              defaultValue={organization.business_website}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Input
              label="Business Phone Number"
              name="business_phone_number"
              type="text"
              defaultValue={organization.business_phone_number}
            />
          </div>
        </div>
      </FormLayout>
    </Form>
  );
};
